'use client'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faQrcode } from '@fortawesome/free-solid-svg-icons'
import {
  ButtonLG,
  Container,
  H1,
  Lead,
  PoweredBy,
  Section,
  Spotlight,
  Stagger,
  TitleWords,
} from 'design-studio'
import { NavigationData } from '@/components/data-links'

const HERO_BADGES = [
  { label: 'Free trial available', dot: 'bg-blue-400' },
  {
    label: 'Requirements · source code · tests · logs unified',
    dot: 'bg-teal-400',
  },
  {
    label: 'Semantic observability included',
    dot: 'bg-violet-400',
  },
] as const

export function SectionHero() {
  return (
    <Section className="relative">
      <Container className="relative z-10 max-w-6xl pt-24 pb-12 lg:pt-[calc(6rem+clamp(4rem,calc(15vh),9rem))] lg:pb-[clamp(4rem,calc(15vh),9rem)]">
        <Spotlight
          color="from-blue-950/90"
          className="fixed -top-224 -left-120 z-0 size-512"
        />
        <Stagger
          step={0.12}
          className="relative z-10 mr-auto flex max-w-3xl flex-col justify-center space-y-6"
        >
          <H1 className="w-full leading-normal opacity-95 lg:text-6xl!">
            <TitleWords>
              Discover and refine mathematical models of your software.
            </TitleWords>
          </H1>
          <Lead className="text-lg">
            SpecLogician helps developers and LLM coding agents discover
            behavioral structure from requirements, source code, scenarios,
            tests, logs, and operational traces — then turn it into a shared
            semantic model for reasoning, verification, semantic observability,
            and test generation.
          </Lead>

          <div className="mt-6 flex flex-col gap-9 md:justify-between lg:flex-row lg:items-center">
            <div className="flex gap-3">
              <ButtonLG
                href="#getting-started"
                variant="none"
                ariaLabel="Start now - go to getting started section"
                classNames="bg-teal-700 font-bold whitespace-nowrap text-white hover:bg-teal-600 max-w-max"
              >
                <FontAwesomeIcon icon={faQrcode} className="h-4 w-4" />
                Start now{' '}
                <FontAwesomeIcon
                  icon={faArrowRight}
                  className="h-4 w-4 -translate-x-1 opacity-0 transition-all duration-200 ease-out group-hover:translate-x-1 group-hover:opacity-100"
                />
              </ButtonLG>
              <ButtonLG
                href={NavigationData.docs.href}
                variant="secondary"
                ariaLabel={NavigationData.docs['aria-label']}
                classNames="backdrop-blur-sm max-w-max border-white/20!"
              >
                Learn more
              </ButtonLG>
            </div>
            <PoweredBy />
          </div>

          <div className="mt-16 flex flex-wrap items-center gap-2 md:mt-28">
            {HERO_BADGES.map(({ label, dot }) => (
              <span
                key={label}
                className="flex items-center gap-1.5 rounded-full border border-slate-400/30 bg-slate-200/60 px-3 py-1 font-mono text-xs text-slate-600 backdrop-blur-sm dark:border-slate-700/50 dark:bg-slate-800/50 dark:text-slate-300"
              >
                <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
                {label}
              </span>
            ))}
          </div>
        </Stagger>
      </Container>
    </Section>
  )
}
